import type { NavigateFunction } from 'react-router-dom';
import type { Dispatch } from 'redux';

import Token from '@/utils/token';
import { setTokens } from '@/store/auth';
import { setFkoDegraded } from '@/store/system';
import { LoginResponse } from '@/services/api.types';
import { readFkoDegraded } from '@/services/fko-status';
import {
  TEMPORARY,
  MFA_DEST_MASKED,
  LDOC_PENDING,
  LDOC_FLOW,
  REDIRECT_SEARCH_PARAM_KEY,
} from '@/constants/system';
import {
  armMfaResendCountdown,
  clearMfaResendCountdown,
} from '@/pages/Auth/mfa-countdown';

// Backend-enforced multistep login transaction (ADR-03). Each pending step names
// the next gate via the protojson enum name (live-verified against :10011 —
// `next: "STEP_MFA"`). PWD (forced change) and LDOC (TOS/PP) follow the same family.
export const LOGIN_STEP = {
  MFA: 'STEP_MFA',
  PWD: 'STEP_PWD',
  LDOC: 'STEP_LDOC',
} as const;

// Which NonAuthedContainer page renders each gate. The tmp step token lives in
// localStorage (TEMPORARY), never in the auth store token — so these pages stay
// reachable before a terminal token exists (non-authed-container gates on
// token.access only).
const STEP_ROUTE: Record<string, string> = {
  [LOGIN_STEP.MFA]: '/login-sms',
  [LOGIN_STEP.PWD]: '/reset-pass',
  [LOGIN_STEP.LDOC]: '/tos-and-pp',
};

// Which login flow this response came out of. Only the LDOC step cares: the consent
// page has two accept endpoints to choose between and the choice is decided by the
// tmp ticket's audience, which the payload does not expose (batch 004, ADR-9).
// Defaults to 'password' — every pre-existing caller is a password-flow one.
export type LoginFlow = 'password' | 'sso';

type AdvanceLoginStepArgs = {
  resp: LoginResponse;
  navigate: NavigateFunction;
  dispatch: Dispatch;
  redirect?: string | null;
  account?: string;
  flow?: LoginFlow;
};

// The login return target (`/login?redirect=<url>`) re-enters the app from the
// address bar, so it is attacker-controlled input, and this is the only place that
// follows it — every consumer page, and the pending branch below, funnel here.
// Unchecked it made the portal a credential-phishing springboard: the real tenant
// domain in the address bar, the real login form, then the attacker's page.
//
// Parsed against the RUNTIME origin, and compared against nothing else. Since
// ADR-07 one build serves every `<env>.<tenant>.levvel.cloud` with the tenant
// decided by the request Host, so a hard-coded host, a `levvel.cloud` suffix test
// or an allow-list array would reject every legitimate redirect on every other
// tenant's domain. The base is also what lets a relative path — the ordinary case —
// resolve at all, and one origin comparison then covers protocol-relative hosts,
// `javascript:` (opaque `"null"` origin) and a tenant name parked in the userinfo
// position without enumerating any of them.
const isSameOriginTarget = (redirect: string): boolean => {
  try {
    const target = new URL(redirect, window.location.origin);
    return target.origin === window.location.origin;
  } catch {
    // Unparseable is untrusted. Nothing reaches replace() on a guess.
    return false;
  }
};

const goToRedirectOrHome = (
  navigate: NavigateFunction,
  redirect?: string | null
) => {
  if (!redirect) {
    navigate('/');
    return;
  }
  if (!isSameOriginTarget(redirect)) {
    // Console only, same idiom as the unknown-gate branch below. The user is put
    // on the home page: a visible notice would be a UI change, and there is no
    // action for them to take.
    console.error(
      `goToRedirectOrHome: refusing a redirect target outside this origin "${redirect}"`
    );
    navigate('/');
    return;
  }
  // The original value, not the normalised URL — the producers put a full
  // `window.location.href` in the parameter and re-serialising it would be an
  // unasked-for behaviour change.
  window.location.replace(redirect);
};

// Drive the login transaction one step forward off the step response body (never
// off JWT claims — the lean token carries no flow signal). `completed` → persist
// the terminal lean token + land; `pending` → stash this step's tmp token and
// route to the next gate page keyed by `next`.
export function advanceLoginStep({
  resp,
  navigate,
  dispatch,
  redirect,
  account,
  flow = 'password',
}: AdvanceLoginStepArgs): void {
  if (resp.completed?.token) {
    const token = resp.completed.token;
    Token.saveAccessToken(token);
    dispatch(setTokens({ access: token, refresh: '' }));
    // FKA org delimitation R3. Raised here rather than in login.tsx because
    // every flow — password, SSO callback, MFA, forced password change,
    // consent — funnels its terminal response through this branch; the SSO
    // page alone would miss the gated ones. Dispatched UNCONDITIONALLY so a
    // later clean login clears a flag the user has already been warned about.
    //
    // COMPLETED BRANCH ONLY, though the wire flag is on pending replies too:
    // a user still held at a gate has not entered the session the notice is
    // about, and the same flag comes down again on the reply that completes.
    dispatch(setFkoDegraded(readFkoDegraded(resp)));
    localStorage.removeItem(TEMPORARY);
    localStorage.removeItem(MFA_DEST_MASKED);
    localStorage.removeItem(LDOC_PENDING);
    // Dies with the rest of the step scratch: a stale marker would send the NEXT
    // login's consent step to the wrong accept endpoint and 401 it.
    localStorage.removeItem(LDOC_FLOW);
    // Same reason, one step further: the resend cooldown is scratch belonging to
    // THIS login transaction. Left behind (it was, until PD-2932 — nothing in the
    // app ever removed it) the next login reads an hours-old instant, computes a
    // negative remainder, and paints a greyed-out `Resend(0s)` on arrival.
    clearMfaResendCountdown();
    goToRedirectOrHome(navigate, redirect);
    return;
  }

  const pending = resp.pending;
  if (pending?.tmpToken) {
    localStorage.setItem(TEMPORARY, pending.tmpToken);
    if (pending.mfa?.destMasked) {
      localStorage.setItem(MFA_DEST_MASKED, pending.mfa.destMasked);
    }
    if (pending.next === LOGIN_STEP.MFA) {
      // A reply that parks the user at the MFA gate means a verification code has
      // just been SENT — POST /v1/login sends the first one before /login-sms is
      // ever rendered. This is the only place that observes that fact, so this is
      // where the resend cooldown gets armed (PD-2932).
      //
      // NOT on the SMS page's mount: mount means "the user is looking at the
      // page", which has no causal link to a code going out. Arming there would
      // restart the 60s on every F5, i.e. hand the user a bypass. Arming on the
      // send, against an absolute instant, makes a refresh show the REAL
      // remaining seconds.
      armMfaResendCountdown();
    }
    if (pending.legal) {
      // LDOC step: stash the pending tos/pp list (JSON) for /tos-and-pp, plus which
      // flow produced it — that decides which accept endpoint the page calls
      // (ADR-9). Recorded here because this is the only point that knows.
      localStorage.setItem(LDOC_PENDING, JSON.stringify(pending.legal.pending));
      localStorage.setItem(LDOC_FLOW, flow);
    } else if (pending.next !== LOGIN_STEP.LDOC) {
      // moved past (or never at) LDOC — drop any stale challenge.
      localStorage.removeItem(LDOC_PENDING);
      localStorage.removeItem(LDOC_FLOW);
    }
    const route = STEP_ROUTE[pending.next];
    if (!route) {
      // Unknown gate — fail loud, send the user back to a clean login.
      console.error(`advanceLoginStep: unknown next step "${pending.next}"`);
      navigate('/login', { state: { account } });
      return;
    }
    const searchString = redirect
      ? `?${REDIRECT_SEARCH_PARAM_KEY}=${encodeURIComponent(redirect)}`
      : '';
    navigate(route + searchString, { state: { account } });
    return;
  }

  // Malformed response — neither branch present.
  console.error(
    'advanceLoginStep: response has neither completed nor pending',
    resp
  );
  navigate('/login', { state: { account } });
}
